import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DraggableAttributes,
} from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import { Box } from '@mui/material';
import type { PlaylistContributorRow } from '@/helpers/contributor-playlist.helper';
import { useReorderContributorPlaylistMutation } from '@/state/mutations/contributors/use-reorder-contributor-playlist.ts';
import { useNotifications } from '@/hooks/use-notifications';
import { useTranslations } from '@/i18n/use-translations';

const DRAG_HANDLE_SIZE_PX = 22;
const SORTABLE_ROW_MARGIN = '0.4rem 0';

const playlistRowsOrderKey = (rows: PlaylistContributorRow[]) => rows.map((row) => row.roleId).join('\0');

type SortableContributorRowProps = {
  roleId: string;
  showDragHandle: boolean;
  sortableDisabled: boolean;
  children: (dragHandle: ReactNode | undefined) => ReactNode;
};

const PlaylistDragHandle = ({
  isDragging,
  interactionDisabled,
  attributes,
  listeners,
}: {
  isDragging: boolean;
  interactionDisabled: boolean;
  attributes: DraggableAttributes;
  listeners: SyntheticListenerMap | undefined;
}) => {
  const t = useTranslations();

  return (
    <Box
      component="button"
      type="button"
      aria-label={t('contributors.reorderDragHandleAria')}
      sx={{
        'flexShrink': 0,
        'display': 'inline-flex',
        'alignItems': 'center',
        'justifyContent': 'center',
        'width': DRAG_HANDLE_SIZE_PX,
        'minWidth': DRAG_HANDLE_SIZE_PX,
        'height': DRAG_HANDLE_SIZE_PX,
        'p': 0,
        'm': 0,
        'border': 0,
        'borderRadius': 0.5,
        'bgcolor': 'transparent',
        'color': 'action.disabled',
        'cursor': interactionDisabled ? 'default' : isDragging ? 'grabbing' : 'grab',
        'pointerEvents': interactionDisabled ? 'none' : 'auto',
        'touchAction': 'none',
        'WebkitTapHighlightColor': 'transparent',
        '&:hover': { color: 'action.active', bgcolor: 'action.hover' },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 1,
        },
      }}
      {...attributes}
      {...listeners}
      onClick={(e) => e.stopPropagation()}
    >
      <DragHandleIcon sx={{ fontSize: 16, transform: 'rotate(90deg)' }} />
    </Box>
  );
};

const SortableContributorRow = ({
  roleId,
  showDragHandle,
  sortableDisabled,
  children,
}: SortableContributorRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: roleId,
    disabled: sortableDisabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const dragHandle = showDragHandle ? (
    <PlaylistDragHandle
      isDragging={isDragging}
      interactionDisabled={sortableDisabled}
      attributes={attributes}
      listeners={listeners}
    />
  ) : undefined;

  return (
    <Box ref={setNodeRef} style={style} sx={{ minWidth: 0, m: SORTABLE_ROW_MARGIN }}>
      {children(dragHandle)}
    </Box>
  );
};

export const SortablePlaylistContributorList = ({
  rows: initialRows,
  playlistId,
  canReorder,
  children,
}: {
  rows: PlaylistContributorRow[];
  playlistId: string;
  canReorder: boolean;
  children: (row: PlaylistContributorRow, dragHandle?: ReactNode) => ReactNode;
}) => {
  const t = useTranslations();
  const notification = useNotifications();
  const [rows, setRows] = useState(initialRows);
  const optimisticOrderKeyRef = useRef<string | null>(null);
  const { mutateAsync: reorderPlaylist, isPending: isReordering } = useReorderContributorPlaylistMutation();

  useEffect(() => {
    const incomingKey = playlistRowsOrderKey(initialRows);
    if (optimisticOrderKeyRef.current !== null) {
      if (incomingKey === optimisticOrderKeyRef.current) {
        optimisticOrderKeyRef.current = null;
      }
      return;
    }
    setRows(initialRows);
  }, [initialRows]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || isReordering) {
        return;
      }

      const oldIndex = rows.findIndex((row) => row.roleId === active.id);
      const newIndex = rows.findIndex((row) => row.roleId === over.id);
      if (oldIndex < 0 || newIndex < 0) {
        return;
      }

      const nextRows = arrayMove(rows, oldIndex, newIndex);
      const previousRows = rows;
      const nextOrderKey = playlistRowsOrderKey(nextRows);
      optimisticOrderKeyRef.current = nextOrderKey;
      setRows(nextRows);

      try {
        await reorderPlaylist({
          playlistId,
          refs: nextRows.map((row, index) => ({
            roleId: row.roleId,
            sequence: index + 1,
          })),
        });
      } catch {
        optimisticOrderKeyRef.current = null;
        setRows(previousRows);
        notification.notifyError(t('contributors.notification.reorderError'));
      }
    },
    [rows, isReordering, playlistId, reorderPlaylist, notification, t],
  );

  const showDragHandle = canReorder && rows.length > 1;
  const sortableDisabled = !showDragHandle || isReordering;
  const sortableIds = rows.map((row) => row.roleId);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={(event) => {
        void handleDragEnd(event);
      }}
    >
      <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
        {rows.map((row) => (
          <SortableContributorRow
            key={row.roleId}
            roleId={row.roleId}
            showDragHandle={showDragHandle}
            sortableDisabled={sortableDisabled}
          >
            {(dragHandle) => children(row, dragHandle)}
          </SortableContributorRow>
        ))}
      </SortableContext>
    </DndContext>
  );
};
